import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import { Bot } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';

const AIBasketSplit = ({ aiSplit, onSplitChange, totalAiAllocation, totalCapital }) => {
  const { t } = useLanguage();
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Bots default order: Sigmai 40%, Sugair 35%, Jairvis 25%
  const bots = [
    { 
      key: 'sigmai', 
      name: t('sigmai'), 
      description: `3.48% ${t('monthlyReturns')}`,
      defaultSplit: 40
    },
    { 
      key: 'sugair', 
      name: t('sugair'), 
      description: `4.50% ${t('monthlyReturns')}`,
      defaultSplit: 35
    },
    { 
      key: 'jairvis', 
      name: t('jairvis') || t('jarvis'),
      description: `3.90% ${t('monthlyReturns')}`,
      fallbackKey: 'jarvis',
      defaultSplit: 25
    }
  ];

  const aiSplitSum = Object.values(aiSplit).reduce((sum, val) => sum + val, 0);
  const aiAllocationAmount = totalCapital * (totalAiAllocation / 100);

  return (
    <Card className="bg-[#040816] border-[#0677aa] shadow-lg" data-testid="ai-basket-split">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-[#00f4ff] flex items-center gap-2 text-lg">
            <Bot className="w-5 h-5" />
            {t('aiSplit')}
          </CardTitle>
          <Badge variant="outline" className="border-[#00f4ff] text-[#00f4ff]">
            {formatCurrency(aiAllocationAmount)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {Math.abs(aiSplitSum - 100) > 0.1 && (
          <div className="text-xs text-yellow-400 bg-yellow-900/20 p-2 rounded border border-yellow-500/50">
            {t('aiSplitMustSum')} {aiSplitSum.toFixed(1)}%
          </div>
        )}
        
        {bots.map(bot => {
          // Get value with backward compatibility
          const splitValue = aiSplit[bot.key] !== undefined ? aiSplit[bot.key] : (aiSplit[bot.fallbackKey] || 0);
          const botAmount = aiAllocationAmount * (splitValue / 100);
          
          return (
            <div key={bot.key} className="space-y-2" data-testid={`ai-bot-${bot.key}`}>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium text-white">
                    {bot.name}
                  </Label>
                  <div className="text-xs text-[#0677aa]">{bot.description}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-[#00f4ff]">
                    {splitValue.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-300">
                    {formatCurrency(botAmount)}
                  </div>
                </div>
              </div>
              <Slider
                value={[splitValue]}
                onValueChange={(value) => onSplitChange(bot.key, value)}
                max={100}
                step={1}
                className="w-full"
                data-testid={`slider-${bot.key}`}
              />
            </div>
          );
        })}
        
        {/* Visual split bar - Order: Sigmai, Sugair, Jairvis */}
        <div className="mt-6 p-4 bg-gradient-to-br from-[#040816] to-[#0b1c38] rounded-xl border border-[#00f4ff]/10">
          <div className="text-xs font-semibold text-[#9EB4C0] mb-3 uppercase tracking-wide">{t('aiAllocationBreakdown')}:</div>
          <div className="flex h-6 bg-[#07121E] rounded-full overflow-hidden shadow-inner border border-[#00f4ff]/20">
            <div 
              className="transition-all duration-500 relative group"
              style={{ 
                width: `${aiSplit.sigmai || 40}%`,
                background: 'linear-gradient(90deg, #ff3030 0%, #ff6060 100%)',
                boxShadow: '0 0 10px rgba(255,48,48,0.4)'
              }}
            >
              <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
            <div 
              className="transition-all duration-500 relative group"
              style={{ 
                width: `${aiSplit.sugair || 35}%`,
                background: 'linear-gradient(90deg, #22dd55 0%, #4dea7d 100%)',
                boxShadow: '0 0 10px rgba(34,221,85,0.4)'
              }}
            >
              <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
            <div 
              className="transition-all duration-500 relative group"
              style={{ 
                width: `${aiSplit.jairvis !== undefined ? aiSplit.jairvis : (aiSplit.jarvis || 25)}%`,
                background: 'linear-gradient(90deg, #00f4ff 0%, #4affff 100%)',
                boxShadow: '0 0 10px rgba(0, 244, 255, 0.4)'
              }}
            >
              <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
          </div>
          <div className="flex justify-between mt-3 text-xs font-medium">
            <span className="text-[#ff3030] flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[#ff3030] shadow-sm shadow-[#ff3030]"></span>
              {t('sigmai')} {aiSplit.sigmai || 40}%
            </span>
            <span className="text-[#22dd55] flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[#22dd55] shadow-sm shadow-[#22dd55]"></span>
              {t('sugair')} {aiSplit.sugair || 35}%
            </span>
            <span className="text-[#00f4ff] flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[#00f4ff] shadow-sm shadow-[#00f4ff]"></span>
              {t('jairvis') || t('jarvis')} {aiSplit.jairvis !== undefined ? aiSplit.jairvis : (aiSplit.jarvis || 25)}%
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AIBasketSplit;
